import { useState, useEffect, useMemo, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface ParticipantRecord {
  id: string;
  team_id: string;
  full_name: string;
  contact_number: string;
  email: string;
  year_of_study: string;
  department: string;
  is_leader: boolean;
  team_name: string;
  college_name: string;
}

export default function Participants() {
  const [participants, setParticipants] = useState<ParticipantRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');
  const [collegeFilter, setCollegeFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data: parts, error: partErr } = await supabase.from('participants').select('id, team_id, full_name, contact_number, email, year_of_study, department, course_type, is_leader').order('full_name');
      if (partErr) throw partErr;

      const { data: teams, error: teamsErr } = await supabase.from('teams').select('id, team_name, college_name');
      if (teamsErr) throw teamsErr;

      const teamMap: Record<string, { team_name: string; college_name: string }> = {};
      (teams || []).forEach((t) => { teamMap[t.id] = { team_name: t.team_name, college_name: t.college_name }; });

      const merged: ParticipantRecord[] = (parts || []).map((p) => ({
        ...p,
        team_name: teamMap[p.team_id]?.team_name || 'Unknown Team',
        college_name: teamMap[p.team_id]?.college_name || 'Unknown College',
      }));
      setParticipants(merged);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const colleges = useMemo(() => [...new Set(participants.map((p) => p.college_name))].sort(), [participants]);
  const years = useMemo(() => [...new Set(participants.map((p) => p.year_of_study))].sort(), [participants]);

  const filtered = useMemo(() => {
    let result = participants;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((p) => p.full_name.toLowerCase().includes(q) || p.department.toLowerCase().includes(q) || p.team_name.toLowerCase().includes(q));
    }
    if (collegeFilter) result = result.filter((p) => p.college_name === collegeFilter);
    if (yearFilter) result = result.filter((p) => p.year_of_study === yearFilter);
    return result;
  }, [participants, search, collegeFilter, yearFilter]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  return (
    <AuthGuard requiredRole={['admin', 'organizer']}>
      <AdminLayout title="Participants" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Participants' }]}>
        {fetchError && (
          <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            Failed to load: {fetchError}. <button onClick={fetchData} className="underline font-medium cursor-pointer">Retry</button>
          </div>
        )}

        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 mb-5">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between mb-4">
            <div className="relative w-full lg:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} placeholder="Search by name, department, team..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none" />
            </div>
            <div className="flex flex-wrap gap-2">
              <select value={collegeFilter} onChange={(e) => { setCollegeFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Colleges</option>
                {colleges.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={yearFilter} onChange={(e) => { setYearFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Years</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-foreground-400">{loading ? 'Loading...' : `${filtered.length} participant${filtered.length !== 1 ? 's' : ''}`}</p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      {['Name', 'Team', 'College', 'Department', 'Year', 'Contact', 'Email', 'Role'].map((col) => (
                        <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((p) => (
                      <tr key={p.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground-900 whitespace-nowrap">{p.full_name}</td>
                        <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{p.team_name}</td>
                        <td className="px-4 py-3 text-foreground-600 whitespace-nowrap text-xs">{p.college_name}</td>
                        <td className="px-4 py-3 text-foreground-600 whitespace-nowrap text-xs">{p.department}</td>
                        <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{p.year_of_study}</td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">{p.contact_number}</td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">{p.email}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {p.is_leader ? <span className="inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold bg-primary-100 text-primary-700">Leader</span> : <span className="text-foreground-400 text-xs">Member</span>}
                        </td>
                      </tr>
                    ))}
                    {paginated.length === 0 && (
                      <tr><td colSpan={8} className="px-4 py-12 text-center text-foreground-400"><i className="ri-inbox-line text-3xl block mb-2"></i>No participants found{participants.length > 0 ? '' : '. Registrations will appear here.'}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-background-200/70">
                  <p className="text-xs text-foreground-400">Page {currentPage} of {totalPages}</p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"><i className="ri-arrow-left-s-line text-sm"></i></button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                      <button key={pg} onClick={() => setCurrentPage(pg)} className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${pg === currentPage ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100'}`}>{pg}</button>
                    ))}
                    <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"><i className="ri-arrow-right-s-line text-sm"></i></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </AuthGuard>
  );
}